import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import PaymentIcon from '@material-ui/icons/Payment';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import DescriptionIcon from '@material-ui/icons/Description';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import useApi from '../../../utils/useApi';
import { formattedCurrency, maskedCurrency } from '../../../utils/utils';
import './saleMobile.css';

const getPaymentIcon = (name = '') => {
  const lower = name.toLowerCase();
  if (lower.includes('dinheiro')) return <AttachMoneyIcon style={{ fontSize: 18 }} />;
  if (lower.includes('crédito') || lower.includes('credito')) return <CreditCardIcon style={{ fontSize: 18 }} />;
  if (lower.includes('débito') || lower.includes('debito')) return <PaymentIcon style={{ fontSize: 18 }} />;
  if (lower.includes('pix')) return <FlashOnIcon style={{ fontSize: 18 }} />;
  if (lower.includes('crediário') || lower.includes('promissória') || lower.includes('fiado') || lower.includes('carnê'))
    return <DescriptionIcon style={{ fontSize: 18 }} />;
  return <PaymentIcon style={{ fontSize: 18 }} />;
};

const SaleMobileCheckout = ({
  open,
  onClose,
  cartValues = {},
  onFinishSale,
  loading = false,
}) => {
  const [selectedMethodId, setSelectedMethodId] = useState('3');
  const [amountPaid, setAmountPaid] = useState(0);
  const [plots, setPlots] = useState(1);
  const [interestPercent, setInterestPercent] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [paymentMethodsLoad, paymentMethodsInfo] = useApi({
    method: 'get',
    url: '/payments/methods',
  });

  const baseTotal = Number(cartValues.total || 0);

  // Cálculo de acréscimo de juros
  const interestIncrease = useMemo(() => {
    const pct = Number(interestPercent || 0);
    if (pct > 0 && baseTotal > 0) {
      return (baseTotal * pct) / 100;
    }
    return 0;
  }, [baseTotal, interestPercent]);

  const finalTotal = useMemo(() => {
    return baseTotal + interestIncrease;
  }, [baseTotal, interestIncrease]);

  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      isSubmittingRef.current = false;
      paymentMethodsLoad();
      setAmountPaid(finalTotal);
      setPlots(typeof cartValues.plots === 'number' ? cartValues.plots : 1);
      setInterestPercent(
        cartValues.interest && typeof cartValues.interest === 'object'
          ? Number(cartValues.interest.percentage || 0)
          : Number(cartValues.interest || 0)
      );
    }
  }, [open, finalTotal]);

  useEffect(() => {
    if (!loading) {
      isSubmittingRef.current = false;
    }
  }, [loading]);

  const paymentMethods = paymentMethodsInfo.data || [];

  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedMethodId) {
      setSelectedMethodId(String(paymentMethods[0].id));
    }
  }, [paymentMethods, selectedMethodId]);

  const selectedMethod = useMemo(() => {
    return paymentMethods.find((m) => String(m.id) === String(selectedMethodId));
  }, [paymentMethods, selectedMethodId]);

  const isCash = useMemo(() => {
    return selectedMethod?.name?.toLowerCase().includes('dinheiro');
  }, [selectedMethod]);

  const isCreditOrInstallments = useMemo(() => {
    const name = selectedMethod?.name?.toLowerCase() || '';
    return (
      name.includes('crédito') ||
      name.includes('credito') ||
      name.includes('crediário') ||
      name.includes('carnê') ||
      name.includes('boleto') ||
      name.includes('promissória')
    );
  }, [selectedMethod]);

  const change = useMemo(() => {
    const paid = Number(amountPaid || 0);
    return isCash && paid > finalTotal ? paid - finalTotal : 0;
  }, [isCash, amountPaid, finalTotal]);

  const remaining = useMemo(() => {
    const paid = Number(amountPaid || 0);
    return finalTotal > paid ? finalTotal - paid : 0;
  }, [amountPaid, finalTotal]);

  const handleQuickCash = (billVal) => {
    if (billVal === 'EXACT') {
      setAmountPaid(finalTotal);
    } else {
      setAmountPaid(billVal);
    }
  };

  const handleConfirm = () => {
    if (isSubmittingRef.current || loading) return;
    isSubmittingRef.current = true;

    const paidNumeric = Number(
      amountPaid !== undefined && amountPaid !== null && amountPaid !== ''
        ? amountPaid
        : finalTotal
    );
    const effectivePaid = Math.min(finalTotal, Math.max(0, paidNumeric));
    const paymentData = [
      {
        paymentMethod: String(selectedMethodId),
        amountPaid: maskedCurrency(effectivePaid.toFixed(2)),
      },
    ];

    if (onFinishSale) {
      onFinishSale(paymentData, plots, interestPercent);
    }
  };

  const installmentValue = useMemo(() => {
    const count = Number(plots || 1);
    return count > 0 ? finalTotal / count : finalTotal;
  }, [finalTotal, plots]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '92vh',
          backgroundColor: '#ffffff',
        },
      }}
    >
      <div className="sale-mobile-drawer-content" style={{ height: 'auto', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="sale-mobile-drawer-header" style={{ padding: '10px 14px' }}>
          <div className="sale-mobile-drawer-title">
            <PaymentIcon style={{ color: '#17497f', fontSize: 20 }} />
            <span>Pagamento</span>
          </div>
          <IconButton size="small" onClick={onClose} style={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Corpo do Checkout (Compacto e Sem Scroll) */}
        <div className="sale-mobile-drawer-body" style={{ padding: '10px 14px' }}>
          {/* Card do Total Compacto */}
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              backgroundColor: '#f0f5fa',
              border: '1.5px solid #d0e1f3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Total a Pagar
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#17497f', lineHeight: 1.1 }}>
                {formattedCurrency(finalTotal)}
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              {plots > 1 && (
                <div style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 800 }}>
                  {plots}x de {formattedCurrency(installmentValue)}
                </div>
              )}
              {interestIncrease > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#ea580c', fontWeight: 700 }}>
                  +{formattedCurrency(interestIncrease)} ({interestPercent}%)
                </div>
              )}
              {cartValues.customer?.name && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {`${cartValues.customer.name} ${cartValues.customer.lastName || cartValues.customer.last_name || ''}`.trim()}
                </div>
              )}
            </div>
          </div>

          {/* Formas de Pagamento em Barra Deslizante Horizontal */}
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
            Forma de Pagamento:
          </div>

          <div className="sale-mobile-pay-strip">
            {paymentMethods.map((method) => {
              const isSelected = String(method.id) === String(selectedMethodId);
              return (
                <div
                  key={method.id}
                  className={`sale-mobile-pay-pill ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedMethodId(String(method.id));
                    setAmountPaid(finalTotal);
                  }}
                >
                  {getPaymentIcon(method.name)}
                  <span>{method.name}</span>
                </div>
              );
            })}
          </div>

          {/* Painel Contextual com Ajustes */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              marginTop: 4,
            }}
          >
            {/* Se for Dinheiro: Notas Rápidas + Troco */}
            {isCash && (
              <div style={{ marginBottom: 8 }}>
                <div className="sale-mobile-bills-row" style={{ marginBottom: 8 }}>
                  <div className="sale-mobile-bill-chip" onClick={() => handleQuickCash('EXACT')}>
                    Exato
                  </div>
                  {[10, 20, 50, 100, 200]
                    .filter((val) => val >= finalTotal || val >= 10)
                    .map((val) => (
                      <div
                        key={val}
                        className="sale-mobile-bill-chip"
                        onClick={() => handleQuickCash(val)}
                      >
                        R$ {val}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Grid com Valor Pago, Parcelas e Juros */}
            <Grid container spacing={1}>
              {/* Valor Pago */}
              <Grid item xs={isCreditOrInstallments || showAdvanced ? 12 : 12}>
                <CurrencyTextField
                  label="Valor Pago / Recebido"
                  variant="outlined"
                  size="small"
                  value={amountPaid}
                  currencySymbol="R$"
                  outputFormat="number"
                  decimalCharacter=","
                  digitGroupSeparator="."
                  onChange={(event, value) => setAmountPaid(Number(value || 0))}
                  fullWidth
                />
              </Grid>

              {/* Parcelas e Juros (Exibido para Cartão/Carnê ou se ativado) */}
              {(isCreditOrInstallments || showAdvanced) && (
                <>
                  <Grid item xs={6}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="plots-select-label">Parcelas</InputLabel>
                      <Select
                        labelId="plots-select-label"
                        value={plots}
                        onChange={(e) => setPlots(Number(e.target.value))}
                        label="Parcelas"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}x {n === 1 ? '(À vista)' : `de ${formattedCurrency(finalTotal / n)}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Juros %"
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="0"
                      value={interestPercent || ''}
                      onChange={(e) => {
                        const val = Math.max(0, Number(e.target.value || 0));
                        setInterestPercent(val);
                      }}
                      InputProps={{
                        endAdornment: <span style={{ color: '#64748b', fontSize: '0.85rem' }}>%</span>,
                      }}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              {/* Botão sutil para exibir parcelamento em outros métodos */}
              {!isCreditOrInstallments && (
                <Grid item xs={12}>
                  <div
                    style={{
                      fontSize: '0.72rem',
                      color: '#17497f',
                      fontWeight: 700,
                      cursor: 'pointer',
                      textAlign: 'right',
                      marginTop: 2,
                    }}
                    onClick={() => setShowAdvanced(!showAdvanced)}
                  >
                    {showAdvanced ? 'Ocultar Parcelas / Juros ▲' : '+ Parcelar ou Aplicar Juros ▾'}
                  </div>
                </Grid>
              )}
            </Grid>

            {/* Avisos Dinâmicos: Troco ou Saldo Restante */}
            {isCash && change > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                  padding: '6px 10px',
                  borderRadius: 6,
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #c8e6c9',
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1b5e20' }}>
                  Troco:
                </span>
                <span style={{ fontSize: '1rem', fontWeight: 900, color: '#2e7d32' }}>
                  {formattedCurrency(change)}
                </span>
              </div>
            )}

            {remaining > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                  padding: '6px 10px',
                  borderRadius: 6,
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fef3c7',
                }}
              >
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e' }}>
                  Saldo restante:
                </span>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#b45309' }}>
                  {formattedCurrency(remaining)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Rodapé Fixo */}
        <div className="sale-mobile-drawer-footer" style={{ padding: '10px 14px' }}>
          <button
            type="button"
            className="sale-mobile-checkout-btn"
            disabled={loading}
            style={(loading || isSubmittingRef.current) ? { pointerEvents: 'none' } : {}}
            onClick={handleConfirm}
          >
            {loading ? (
              <CircularProgress size={22} style={{ color: '#ffffff' }} />
            ) : (
              <>
                <CheckCircleIcon style={{ fontSize: 20 }} />
                <span>Confirmar Venda ({formattedCurrency(finalTotal)})</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default SaleMobileCheckout;
